import React, { useMemo, useRef } from 'react';
import { Table } from 'tea-component';
import moment from 'moment';
import { IBilibiliHashtagItem } from './constants';
import TableCommonHook from '@op/components/TableCommonHook';
import useIntls from '@opm/hooks/useIntls';
import { dealwithBiLiBiLiImage } from '@op/utils/cdnUrl';
import TextClamp from '@opm/components/TextClamp';

const { groupable, scrollable } = Table.addons;

interface IBilibiliHashtagTableProps {
  records: IBilibiliHashtagItem[];
  loading: boolean;
  filter: { start_time?: string; end_time?: string } | null;
  hours: string[];
  total?: number;
  isAllTime: boolean;
}

interface IHashtagMatrixRow {
  rank: number;
  [cellKey: string]: string | number | undefined;
}

/**
 * 从 API records 取最新整点快照时间（list 按 time DESC，取 max 更稳妥）
 */
function getLatestRecordTime(records: IBilibiliHashtagItem[]): moment.Moment | null {
  if (!records.length) return null;
  return records.reduce((latest, record) => {
    const current = moment(record.time);
    return current.isAfter(latest) ? current : latest;
  }, moment(records[0].time));
}

/** All Time 下：仅最新数据日当天，隐藏晚于 API 最新整点的列 */
function isFutureAllTimeHour(
  date: string,
  hour: string,
  isAllTime: boolean,
  latestDataTime: moment.Moment | null,
): boolean {
  if (!isAllTime || !latestDataTime) return false;
  if (!moment(date, 'YYYY-MM-DD').isSame(latestDataTime, 'day')) return false;
  return Number(hour) > latestDataTime.hour();
}

/**
 * 从 filter 的 start_time/end_time 提取日期列表
 */
function getDateList(filter: { start_time?: string; end_time?: string } | null): string[] {
  if (!filter?.start_time || !filter?.end_time) return [];
  const start = moment(filter.start_time).startOf('day');
  const end = moment(filter.end_time).startOf('day');
  const dates: string[] = [];
  const current = start.clone();
  while (current.isSameOrBefore(end, 'day')) {
    dates.push(current.format('YYYY-MM-DD'));
    current.add(1, 'day');
  }
  return dates;
}

/**
 * 将 flat records 转为按 rank 分组的矩阵行
 * 每个单元格存三份数据: hashtag / hashtag_en / tag_icon
 */
function transformToMatrix(
  records: IBilibiliHashtagItem[],
  dates: string[],
  hours: string[],
): IHashtagMatrixRow[] {
  // 建立 lookup: "rank_date_hour" -> record
  const lookup = new Map<string, IBilibiliHashtagItem>();
  records.forEach((record) => {
    const m = moment(record.time);
    const dateStr = m.format('YYYY-MM-DD');
    const hourStr = String(m.hours()).padStart(2, '0');
    const key = `${record.rank}_${dateStr}_${hourStr}`;
    lookup.set(key, record);
  });
  // 收集所有 rank 去重排序
  const ranks = [...new Set(records.map((r) => r.rank))].sort((a, b) => a - b);

  // 生成矩阵行
  return ranks.map((rank) => {
    const row: IHashtagMatrixRow = { rank };
    dates.forEach((date) => {
      hours.forEach((hour) => {
        const cellKey = `${date}_${hour}`;
        const lookupKey = `${rank}_${date}_${hour}`;
        const record = lookup.get(lookupKey);
        row[cellKey] = record?.hashtag || '';
        row[`${cellKey}__en`] = record?.hashtag_en || '';
        row[`${cellKey}__icon`] = record?.tag_icon || '';
      });
    });
    return row;
  });
}

export default function BilibiliHashtagTable({
  records,
  loading,
  filter,
  hours,
  total = 0,
  isAllTime,
}: IBilibiliHashtagTableProps) {
  const { isEn, intl } = useIntls();

  // 用 ref 保存最新的 filter/hours，不作为 useMemo 依赖
  // 这样 filter/hours 变化时不会触发重算，只在 records 变化（数据返回后）才重算
  const filterRef = useRef(filter);
  const hoursRef = useRef(hours);
  filterRef.current = filter;
  hoursRef.current = hours;

  // 依赖 records 而非 filter：仅在数据返回后才重新计算日期和时段
  const dates = useMemo(() => getDateList(filterRef.current).reverse(), [records]);

  // 表头时间列倒序（最新时段在前）
  const sortedHours = useMemo(() => [...hoursRef.current].reverse(), [records]);

  const matrixData = useMemo(
    () => transformToMatrix(records, dates, sortedHours),
    [records, dates, sortedHours],
  );

  // 以 API 返回的最新 time 为截止，避免用客户端时钟与数据不一致
  const latestDataTime = useMemo(() => getLatestRecordTime(records), [records]);

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        key: 'rank',
        header: intl("Rank"),
        width: 82,
        align: 'center' as const,
        fixed: 'left' as const,
        render: (row: IHashtagMatrixRow) => {
          return (
            <div className="dbom-hashtag-rank dbom-hashtag-rank--bili">
              <p className={`dbom-rank dbom-rank0${row.rank}`}>{row.rank}</p>
            </div>
          );
        },
      },
    ];

    dates.forEach((date) => {
      sortedHours.forEach((hour) => {
        const cellKey = `${date}_${hour}`;
        if (isFutureAllTimeHour(date, hour, isAllTime, latestDataTime)) {
          return;
        }
        cols.push({
          key: cellKey,
          header: `${hour}:00`,
          width: 260,
          align: 'left' as const,
          render: (row: IHashtagMatrixRow) => {
            const hashtag = row[cellKey] as string;
            const hashtagEn = row[`${cellKey}__en`] as string;
            const tagIcon = row[`${cellKey}__icon`] as string;
            const mainText = isEn ? hashtagEn : hashtag;
            if (!mainText) return '-';
            return (
              <div className="dbom-hashtag-title-cell">
                <TextClamp
                  rowNum={1}
                  text={mainText}
                  placement="top"
                />
                {tagIcon && (
                  <img
                    className="dbom-hashtag-tag-icon"
                    src={dealwithBiLiBiLiImage(tagIcon)}
                  />
                )}
              </div>
            );
          },
        });
      });
    });
    return cols;
  }, [dates, sortedHours, isEn, isAllTime, latestDataTime, intl]);

  const addons = useMemo(() => {
    const list: any[] = [scrollable({})];
    // 多日时才启用日期分组表头，单日无需合并
    if (dates.length > 1) {
      list.unshift(
        groupable({
          headerGroups: dates.map((date) => ({
            key: date,
            title: date,
            align: 'center' as const,
            children: sortedHours
              .map((hour) => {
                if (isFutureAllTimeHour(date, hour, isAllTime, latestDataTime)) {
                  return null;
                }
                return `${date}_${hour}`;
              })
              .filter(Boolean) as string[],
          })),
        }),
      );
    }
    return list;
  }, [dates, sortedHours, isAllTime, latestDataTime]);

  return (
    <div className="dbom-bilibili-hashtag-table">
      <TableCommonHook
        records={matrixData}
        recordKey={(row: IHashtagMatrixRow) => String(row.rank)}
        columns={columns}
        loading={loading}
        total={total}
        bordered="all"
        hasPageable={false}
        addons={addons}
      />
    </div>
  );
}
